import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Table, ProgressBar } from 'react-bootstrap';
import { showToast } from '../utils';
import { URLS } from '../../urls';
import CloseIcon from 'mdi-react/CloseIcon';
import FileUploadIcon from 'mdi-react/FileUploadIcon';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import AlertCircleIcon from 'mdi-react/AlertCircleIcon';
import { toast } from 'react-toastify';
import ToastUI from '../components/ToastUI';

const ImportProspectsModal = ({ show, onHide, selectedProject, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  // Required columns mapping
  const requiredColumns = {
    taskName: 'Task Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    taskDetail: 'Address',
    taskDesc: 'Description',
    taskWebsiteAddress: 'Website',
    startDate: 'Start Date',
    dueDate: 'Due Date',
    customerType: 'Customer Type',
    aboutUs: 'How Did You Hear About Us',
    productInterest: 'Product Interest',
    position: 'Position',
    status: 'Status',
  };

  const customerTypes = ['Walk In', 'Online', 'Referral', 'Advertisement', 'Social Media'];
  const aboutUsOptions = [
    'Flyers',
    'Website',
    'Social Media',
    'Referral',
    'Advertisement',
    'Other',
  ];
  const statusOptions = ['AWAITING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'csv') {
      toast.error(<ToastUI message = 'Please select a CSV file only' type= 'error'/>);
      return;
    }

    setFile(selectedFile);
    parseCSVFile(selectedFile);
  };

  // Native CSV parser function
  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const data = parseCSV(csvText);
        processData(data);
      } catch (error) {
        toast.error(<ToastUI message = {`Error parsing CSV file:  ${error.message}`} type= 'error'/>);
      }
    };
    reader.readAsText(file);
  };

  // Simple CSV parser using native JavaScript
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = parseCSVLine(lines[0]);
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0) continue; // Skip empty lines

      const row = {};
      headers.forEach((header, index) => {
        // Map CSV headers to our expected field names
        const fieldName = getFieldNameFromHeader(header.trim());
        row[fieldName] = values[index] ? values[index].trim() : '';
      });
      data.push(row);
    }

    return data;
  };

  // Parse a single CSV line handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  };

  // Map CSV headers to field names
  const getFieldNameFromHeader = (header) => {
    const headerMap = {
      'task name': 'taskName',
      'company name': 'taskName',
      'first name': 'firstName',
      'last name': 'lastName',
      email: 'email',
      phone: 'phone',
      address: 'taskDetail',
      description: 'taskDesc',
      website: 'taskWebsiteAddress',
      'start date': 'startDate',
      'due date': 'dueDate',
      'customer type': 'customerType',
      'how did you hear about us': 'aboutUs',
      'product interest': 'productInterest',
      position: 'position',
      status: 'status',
    };

    return headerMap[header.toLowerCase()] || header.toLowerCase().replace(/\s+/g, '');
  };

  const processData = (data) => {
    if (!data || data.length === 0) {
      toast.error(<ToastUI message = 'No data found in the file' type= 'error'/>);
      return;
    }

    const errors = [];
    const processedData = data.map((row, index) => {
      const rowErrors = validateRow(row, index + 1);
      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
      }
      return {
        ...row,
        rowIndex: index + 1,
        isValid: rowErrors.length === 0,
      };
    });

    setParsedData(processedData);
    setValidationErrors(errors);
  };

  const validateRow = (row, rowIndex) => {
    const errors = [];

    // Check required fields
    if (!row.taskName?.trim()) {
      errors.push(`Row ${rowIndex}: Task Name is required`);
    }
    if (!row.firstName?.trim()) {
      errors.push(`Row ${rowIndex}: First Name is required`);
    }
    if (!row.lastName?.trim()) {
      errors.push(`Row ${rowIndex}: Last Name is required`);
    }
    if (!row.email?.trim()) {
      errors.push(`Row ${rowIndex}: Email is required`);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      errors.push(`Row ${rowIndex}: Invalid email format`);
    }
    if (!row.phone?.trim()) {
      errors.push(`Row ${rowIndex}: Phone is required`);
    }

    // Validate enum fields
    if (row.customerType && !customerTypes.includes(row.customerType)) {
      errors.push(
        `Row ${rowIndex}: Invalid Customer Type. Must be one of: ${customerTypes.join(', ')}`
      );
    }
    if (row.aboutUs && !aboutUsOptions.includes(row.aboutUs)) {
      errors.push(
        `Row ${rowIndex}: Invalid "How Did You Hear About Us" value. Must be one of: ${aboutUsOptions.join(
          ', '
        )}`
      );
    }
    if (row.status && !statusOptions.includes(row.status)) {
      errors.push(`Row ${rowIndex}: Invalid Status. Must be one of: ${statusOptions.join(', ')}`);
    }

    // Validate dates
    if (row.startDate && !isValidDate(row.startDate)) {
      errors.push(`Row ${rowIndex}: Invalid Start Date format. Use DD/MM/YYYY`);
    }
    if (row.dueDate && row.dueDate.trim() && !isValidDate(row.dueDate)) {
      errors.push(`Row ${rowIndex}: Invalid Due Date format. Use DD/MM/YYYY`);
    }

    return errors;
  };

  const isValidDate = (dateString) => {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateString)) return false;

    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  };

  const handleImport = async () => {
    if (validationErrors.length > 0) {
      toast.error(<ToastUI message = 'Please fix validation errors before importing' type= 'error'/>);
      return;
    }

    const validRows = parsedData.filter((row) => row.isValid);
    if (validRows.length === 0) {
      toast.error(<ToastUI message = 'No valid rows to import' type= 'error'/>);
      return;
    }

    setIsProcessing(true);
    setImportProgress(0);

    const results = {
      successful: 0,
      failed: 0,
      errors: [],
    };

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const payload = {
          taskName: row.taskName?.trim() || '',
          taskDetail: row.taskDetail?.trim() || '',
          taskDesc: row.taskDesc?.trim() || 'Imported prospect',
          taskWebsiteAddress: row.taskWebsiteAddress?.trim() || '',
          startDate: row.startDate?.trim() || '',
          dueDate: row.dueDate?.trim() || '',
          customerType: row.customerType?.trim() || 'Walk In',
          aboutUs: row.aboutUs?.trim() || 'Other',
          productInterest: row.productInterest?.trim() || '',
          status: row.status?.trim() || 'AWAITING',
          name: {
            first: row.firstName?.trim() || '',
            last: row.lastName?.trim() || '',
          },
          email: row.email?.trim() || '',
          phone: row.phone?.trim() || '',
          position: row.position?.trim() || '',
        };

        const response = await fetch(`${URLS.backendCrm}/create-customer/${selectedProject._id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          results.successful++;
        } else {
          const errorData = await response.json();
          results.failed++;
          results.errors.push(
            `Row ${row.rowIndex}: ${errorData.message || 'Failed to create prospect'}`
          );
        }
      } catch (error) {
        results.failed++;
        results.errors.push(`Row ${row.rowIndex}: ${error.message}`);
      }

      setImportProgress(((i + 1) / validRows.length) * 100);
    }

    setImportResults(results);
    setIsProcessing(false);

    if (results.successful > 0) {
      toast.success(<ToastUI message = {`Successfully imported ${results.successful} prospects`} type= 'success'/>);
      if (onImportComplete) {
        onImportComplete();
      }
    }

    if (results.failed > 0) {
      toast.warning(<ToastUI message = {`${results.failed} prospects failed to import`} type= 'warning'/>);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setValidationErrors([]);
    setImportResults(null);
    setImportProgress(0);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onHide();
  };

  const downloadTemplate = () => {
    const csvContent = [
      'Task Name,First Name,Last Name,Email,Phone,Address,Description,Website,Start Date,Due Date,Customer Type,How Did You Hear About Us,Product Interest,Position,Status',
      'Example Company,John,Doe,john.doe@example.com,1234567890,"123 Main St, City, State",Potential client interested in our services,https://example.com,01/01/2024,31/01/2024,Walk In,Website,Web Development,CEO,AWAITING',
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prospects_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered>
      <Modal.Header>
        <Modal.Title>Import Prospects</Modal.Title>
        <Button variant='link' onClick={handleClose} className='p-0 border-0 bg-transparent'>
          <CloseIcon size={20} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {!file && (
          <div className='text-center py-4'>
            <div className='mb-4'>
              <FileUploadIcon size={48} className='text-muted mb-3' />
              <h5>Upload CSV File</h5>
              <p className='text-muted'>
                Select a CSV file containing prospect data to import into your pipeline.
              </p>
            </div>

            <div className='mb-4'>
              <Button variant='outline-primary' onClick={() => fileInputRef.current?.click()}>
                Choose CSV File
              </Button>
              <input
                ref={fileInputRef}
                type='file'
                accept='.csv'
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className='mb-4'>
              <Button variant='link' onClick={downloadTemplate} className='text-decoration-none'>
                Download CSV Template
              </Button>
            </div>

            <Alert variant='info'>
              <h6>Required CSV Columns:</h6>
              <div className='row'>
                <div className='col-md-6'>
                  <ul className='list-unstyled text-start'>
                    <li>
                      <strong>Task Name</strong> - Company/prospect name
                    </li>
                    <li>
                      <strong>First Name</strong> - Contact first name
                    </li>
                    <li>
                      <strong>Last Name</strong> - Contact last name
                    </li>
                    <li>
                      <strong>Email</strong> - Contact email address
                    </li>
                    <li>
                      <strong>Phone</strong> - Contact phone number
                    </li>
                  </ul>
                </div>
                <div className='col-md-6'>
                  <ul className='list-unstyled text-start'>
                    <li>
                      <strong>Customer Type</strong> - {customerTypes.join(', ')}
                    </li>
                    <li>
                      <strong>How Did You Hear About Us</strong> - {aboutUsOptions.join(', ')}
                    </li>
                    <li>
                      <strong>Status</strong> - {statusOptions.join(', ')}
                    </li>
                    <li>
                      <strong>Start Date</strong> - Format: DD/MM/YYYY
                    </li>
                    <li>
                      <strong>Due Date</strong> - Format: DD/MM/YYYY (optional)
                    </li>
                  </ul>
                </div>
              </div>
            </Alert>
          </div>
        )}

        {file && !isProcessing && !importResults && (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6>File: {file.name}</h6>
              <Button variant='outline-secondary' size='sm' onClick={() => setFile(null)}>
                Choose Different File
              </Button>
            </div>

            {validationErrors.length > 0 && (
              <Alert variant='danger'>
                <h6>
                  <AlertCircleIcon size={16} className='me-2' />
                  Validation Errors:
                </h6>
                <ul className='mb-0'>
                  {validationErrors.slice(0, 10).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {validationErrors.length > 10 && (
                    <li>... and {validationErrors.length - 10} more errors</li>
                  )}
                </ul>
              </Alert>
            )}

            <div className='mb-3'>
              <strong>Preview ({parsedData.length} rows found):</strong>
            </div>

            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size='sm'>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>Task Name</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Customer Type</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 20).map((row, index) => (
                    <tr key={index} className={row.isValid ? '' : 'table-danger'}>
                      <td>{row.rowIndex}</td>
                      <td>
                        {row.isValid ? (
                          <CheckCircleIcon size={16} className='text-success' />
                        ) : (
                          <AlertCircleIcon size={16} className='text-danger' />
                        )}
                      </td>
                      <td>{row.taskName}</td>
                      <td>
                        {row.firstName} {row.lastName}
                      </td>
                      <td>{row.email}</td>
                      <td>{row.phone}</td>
                      <td>{row.customerType}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {parsedData.length > 20 && (
                <p className='text-muted text-center'>... and {parsedData.length - 20} more rows</p>
              )}
            </div>
          </div>
        )}

        {isProcessing && (
          <div className='text-center py-4'>
            <h5>Importing Prospects...</h5>
            <ProgressBar
              now={importProgress}
              label={`${Math.round(importProgress)}%`}
              className='mb-3'
            />
            <p className='text-muted'>Please wait while we import your prospects.</p>
          </div>
        )}

        {importResults && (
          <div className='text-center py-4'>
            <CheckCircleIcon size={48} className='text-success mb-3' />
            <h5>Import Complete</h5>
            <div className='row justify-content-center'>
              <div className='col-md-6'>
                <Alert variant='success'>
                  <strong>{importResults.successful}</strong> prospects imported successfully
                </Alert>
                {importResults.failed > 0 && (
                  <Alert variant='warning'>
                    <strong>{importResults.failed}</strong> prospects failed to import
                    {importResults.errors.length > 0 && (
                      <details className='mt-2'>
                        <summary>View Errors</summary>
                        <ul className='mt-2 mb-0'>
                          {importResults.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </Alert>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!file && (
          <Button variant='secondary' onClick={handleClose}>
            Cancel
          </Button>
        )}

        {file && !isProcessing && !importResults && (
          <>
            <Button variant='secondary' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleImport}
              disabled={validationErrors.length > 0 || parsedData.length === 0}
            >
              Import {parsedData.filter((row) => row.isValid).length} Prospects
            </Button>
          </>
        )}

        {importResults && (
          <Button variant='primary' onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportProspectsModal;
